/**
 * @format
 */

import { camelCase, cloneDeep, find, hasIn, isEmpty, isEqual, isNil, noop } from 'lodash';
import { DocumentVersion } from '@/mixins/builder/document-version-mixin';

export const ElementInitialSize = Object.freeze({
    logo: {
        height: 224,
        width: 400,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 20,
    },

    image: {
        height: 224,
        width: 400,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 20,
    },

    video: {
        height: 230,
        width: 400,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 20,
    },

    text: {
        height: 70,
        width: 150,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 20,
    },

    button: {
        width: 150,
        height: 50,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 20,
    },

    quote: {
        width: 500,
        height: 300,
        aspectRatio: true,
        minHeight: 100,
        minWidth: 165,
    },

    table: {
        width: 500,
        height: 276,
        aspectRatio: true,
        minHeight: 80,
        minWidth: 100,
    },

    spacer: {
        width: 114,
        height: 16,
        aspectRatio: false,
        minHeight: 16,
        minWidth: 20,
    },

    divider: {
        width: 114,
        height: 40,
        aspectRatio: true,
        minHeight: 40,
        minWidth: 40,
    },

    'text-field': {
        width: 300,
        height: 70,
        aspectRatio: true,
        minHeight: 70,
        minWidth: 150,
    },

    signature: {
        width: 300,
        height: 76,
        aspectRatio: true,
        minHeight: 70,
        minWidth: 150,
    },

    checkbox: {
        width: 300,
        height: 56,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 100,
    },

    'date-stamp': {
        width: 300,
        height: 75,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 150,
    },

    'radio-button': {
        width: 300,
        height: 94,
        aspectRatio: true,
        minHeight: 20,
        minWidth: 100,
    },

    'page-break': {
        width: null,
        height: null,
    },
});

export default {
    data() {
        return {
            brandingContext: {
                brandingOptions: [],
                logoBrandingOptions: [],
            },

            boundInputs: [],
            voidConfiguration: null,
        };
    },

    methods: {
        createGlobalConfig(brandingSettings) {
            const {
                heading1,
                heading2,
                heading3,
                heading4,
                heading5,
                heading6,
                image,
                lightImage,
                text,
                links,
                button,
                backgroundColor,
                bulletsColor,
            } = brandingSettings;

            return {
                classes: {
                    heading1: {
                        ...heading1,
                    },
                    heading2: {
                        ...heading2,
                    },
                    heading3: {
                        ...heading3,
                    },
                    heading4: {
                        ...heading4,
                    },
                    heading5: {
                        ...heading5,
                    },
                    heading6: {
                        ...heading6,
                    },
                    image: {
                        ...image,
                    },
                    lightImage: {
                        ...lightImage,
                    },
                    text: {
                        ...text,
                    },
                    link: {
                        ...links,
                    },
                    button: {
                        style: {
                            ...button,
                            computedBackgroundColor: null,
                            computedBorder: null,
                            computedBorderRadius: null,
                            computedAlignment: null,
                            padding: '10px 25px',
                            alignment: 'center',
                        },
                    },
                    canvas: {
                        backgroundColor: {
                            ...backgroundColor,
                            value: '#282828',
                        },
                    },
                    page: {
                        hasSolidBackgroundColor: true,
                        backgroundColor: {
                            ...backgroundColor,
                        },
                        hasBackgroundImage: false,
                        backgroundImageUrl: null,
                        margins: {
                            showMargins: false,
                            allSides: {
                                active: false,
                                value: 80,
                            },
                            perSide: {
                                active: false,
                                top: 80,
                                left: 80,
                                bottom: 80,
                                right: 80,
                            },
                        },
                    },
                    header: {
                        hasSolidBackgroundColor: true,
                        backgroundColor: {
                            ...backgroundColor,
                        },
                        hasBackgroundImage: false,
                        backgroundImageUrl: null,
                        title: cloneDeep(text),
                    },
                    footer: {
                        hasSolidBackgroundColor: true,
                        backgroundColor: {
                            branding: null,
                            enabled: true,
                            type: null,
                            value: '#f2f2f2',
                        },
                        hasBackgroundImage: false,
                        backgroundImageUrl: null,
                        message: cloneDeep(text),
                        numbering: cloneDeep(text),
                    },
                    section: {
                        hasSolidBackgroundColor: true,
                        backgroundColor: {
                            ...backgroundColor,
                        },
                        hasBackgroundImage: false,
                        backgroundImageUrl: null,
                    },
                    bulletsColor: {
                        ...bulletsColor,
                    },
                },
                initialized: false,
            };
        },

        createDefaultBaseConfig() {
            const brandingSettings = this.$helpers.getDefaultDocumentBranding();

            return {
                header: null,
                footer: null,
                pages: [],
                totalPages: null,
                global: this.createGlobalConfig(brandingSettings),
                version: DocumentVersion.V2,
            };
        },

        createFormForPdfFile(pdfFile) {
            const fd = new FormData();
            fd.append('pdf', pdfFile, pdfFile.name);

            return fd;
        },
        /**
         * Element Flip functions
         */

        flipElement(selectedElement, direction) {
            if (direction.toUpperCase() === 'HORIZONTAL') {
                selectedElement.freeformStyle.flip.x *= -1;
            } else {
                selectedElement.freeformStyle.flip.y *= -1;
            }
        },

        /**
         * Element Overlapping functions
         */

        adjustZIndex(selectedElement, pageElements, parentPageId, action) {
            const elementIndex = pageElements.findIndex((element) => {
                return element.id === selectedElement.id;
            });

            if (action === 'forward' && elementIndex < pageElements.length - 1) {
                this.moveElementInArray(pageElements, elementIndex, elementIndex + 1);
            } else if (action === 'backwards' && elementIndex > 0) {
                this.moveElementInArray(pageElements, elementIndex, elementIndex - 1);
            } else if (action === 'front' && elementIndex < pageElements.length - 1) {
                this.moveElementInArray(pageElements, elementIndex, pageElements.length);
            } else if (action === 'back' && elementIndex > 0) {
                this.moveElementInArray(pageElements, elementIndex, 0);
            }

            const parentPage = this.getPageById(parentPageId);
            parentPage.elements = pageElements;
        },

        moveElementInArray(array, fromIndex, toIndex) {
            const [movedObject] = array.splice(fromIndex, 1);
            array.splice(toIndex, 0, movedObject);

            return array;
        },

        /**
         * Element Alignment functions
         */
        setElementPositionBasedInAlignment(
            selectedElement,
            boundingClientRect,
            place,
            pageMargins,
        ) {
            const pageMarginTop = parseInt(pageMargins['margins-top']);
            const pageMarginLeft = parseInt(pageMargins['margins-left']);
            const pageMarginWidth = parseInt(pageMargins['margins-width']);
            const pageMarginHeight = parseInt(pageMargins['margins-height']);

            const elementWidthDiff = boundingClientRect.width - selectedElement.freeformStyle.width;
            const elementHeightDiff =
                boundingClientRect.height - selectedElement.freeformStyle.height;
            const halfElementWidth = selectedElement.freeformStyle.width / 2;
            const halfElementHeight = selectedElement.freeformStyle.height / 2;

            switch (place) {
                case 'top':
                    selectedElement.position.top = pageMarginTop + elementHeightDiff / 2;
                    break;
                case 'bottom':
                    selectedElement.position.top =
                        pageMarginTop +
                        pageMarginHeight +
                        elementHeightDiff / 2 -
                        boundingClientRect.height;
                    break;
                case 'left':
                    selectedElement.position.left = pageMarginLeft + elementWidthDiff / 2;
                    break;
                case 'right':
                    selectedElement.position.left =
                        pageMarginLeft +
                        pageMarginWidth +
                        elementWidthDiff / 2 -
                        boundingClientRect.width;
                    break;
                case 'center':
                    selectedElement.position.left =
                        pageMarginLeft + pageMarginWidth / 2 - halfElementWidth;
                    break;
                case 'middle':
                    selectedElement.position.top =
                        pageMarginTop + pageMarginHeight / 2 - halfElementHeight;
                    break;
                default:
                    break;
            }
        },

        /**
         * Element position config factory
         */

        elementFreeformStylesOf(type) {
            return {
                width: ElementInitialSize[type].width,
                height: ElementInitialSize[type].height,
                scaledWidth: this.$helpers.pixelsToDocumentPoints(ElementInitialSize[type].width),
                scaledHeight: this.$helpers.pixelsToDocumentPoints(ElementInitialSize[type].height),
                flip: {
                    x: 1,
                    y: 1,
                },
                rotation: 0,
            };
        },

        elementPosition() {
            return {
                top: 0,
                left: 0,
                scaledTop: 0,
                scaledLeft: 0,
            };
        },

        /**
         * Element content config factory
         */
        contentOf(type) {
            switch (type) {
                case 'image':
                    return {
                        hasLink: false,
                        link: null,
                        linkType: 'Custom URL',
                        sourceName: null,
                        sourceUrl: null,
                        sourceType: 'UNSET',
                    };
                case 'video':
                    return {
                        sourceName: null,
                        sourceUrl: null,
                        sourceType: 'VIDEO_URL',
                        thumbnail: {
                            sourceUrl: null,
                            sourceName: null,
                        },
                    };
                case 'button':
                    return {
                        text: 'Click here',
                        linkType: 'Custom URL',
                        link: null,
                    };
                case 'text':
                    return {
                        body: null,
                    };
                case 'text-field':
                    return {
                        recipient: this.selectedRecipient
                            ? { id: this.selectedRecipient.id }
                            : null,
                        body: null,
                        placeholder: 'Enter Value',
                        isRequired: false,
                        isMultiline: false,
                        hasLabel: true,
                        labelText: 'Enter Label',
                    };
                case 'signature':
                    return {
                        recipient: this.selectedRecipient
                            ? { id: this.selectedRecipient.id }
                            : null,
                        link: null,
                        placeholder: 'Signature',
                        isRequired: true,
                        hasLabel: true,
                        labelText: 'Signature',
                    };
                case 'checkbox':
                    return {
                        recipient: this.selectedRecipient
                            ? { id: this.selectedRecipient.id }
                            : null,
                        message: `<p><span style='font-family: Roboto, Helvetica, Arial, Tahoma, "Franklin Gothic Medium", sans-serif; font-size: 16px;'>Enter Message</span></p>`,
                        isRequired: false,
                        hasLabel: true,
                        labelText: 'Enter Label',
                        isChecked: false,
                    };
                case 'date-stamp':
                    return {
                        recipient: this.selectedRecipient
                            ? { id: this.selectedRecipient.id }
                            : null,
                        hasLabel: true,
                        labelText: 'Date',
                        placeholder: 'Select a Date',
                        dateFormat: 'MMM DD, YYYY',
                        body: null,
                        isRequired: false,
                    };
                case 'radio-button':
                    return {
                        recipient: this.selectedRecipient
                            ? { id: this.selectedRecipient.id }
                            : null,
                        options: [
                            {
                                id: this.$helpers.createId('opt'),
                                value: 'Option 1',
                            },
                            {
                                id: this.$helpers.createId('opt'),
                                value: 'Option 2',
                            },
                        ],
                        isRequired: false,
                        hasLabel: true,
                        labelText: 'Radio Button',
                        selectedOption: null,
                        recipientSelected: false,
                    };
                case 'quote':
                    return {
                        labelText: 'Quote',
                        hasTax: false,
                        taxValue: 10,
                        hasDiscount: false,
                        discountType: 'total', // total || item
                        totalDiscountValue: 10,
                        totalDiscountType: 'percentage', // percentage || amount
                        itemDiscountValue: 10,
                        itemDiscountType: 'percentage', // percentage || amount
                        hasHeaderRow: true,
                        hasCellBorders: true,
                        solutions: [],
                        upfrontPayment: 0,
                        monthlyPayment: 0,
                        quarterlyPayment: 0,
                        semiAnnualPayment: 0,
                        annualPayment: 0,
                        cost: 0,
                        initialPayment: 0,
                        total: 0,
                        quoteView: {
                            showUpfront: true,
                            showMonthly: true,
                            showQuarterly: true,
                            showSemiAnnual: true,
                            showAnnual: true,
                            showTax: true,
                            showDiscount: true,
                            showTotal: true,
                            showInitialPayment: true,
                        },
                        solutionsView: {
                            showName: true,
                            showDescription: true,
                            showPrice: true,
                            showQuantity: true,
                            showTerm: true,
                            showDiscount: true,
                            showSubtotal: true,
                            showProductImage: true,
                        },
                    };
                case 'table':
                    return {
                        columns: 3,
                        rows: 5,
                        hasHeaderRow: true,
                        hasCellBorders: true,
                        headerRow: {
                            id: this.$helpers.createId('hr'),
                            columns: [],
                            height: 'fit-content',
                            startPosition: null,
                            endPosition: 0,
                        },
                        tableRows: [],
                        initialized: false,
                    };
                case 'logo':
                    return {
                        hasLink: false,
                        link: null,
                        linkType: 'Custom URL',
                        logo: this.getDefaultLogo(),
                        isNew: true,
                    };
                case 'spacer':
                    return {
                        isNew: true,
                    };
                default:
                    return null;
            }
        },

        elementStylesOf(type) {
            switch (type) {
                case 'image':
                case 'video':
                case 'logo':
                    return {
                        display: 'flex',
                        justifyContent: 'center',
                        addSpacing: false,
                        scaledSpacing: '0px',
                        width: 0,
                        height: 'auto',
                        maxWidth: 0,
                        scaledWidth: null,
                    };
                case 'button':
                    return {
                        ...cloneDeep(this.currentGlobalConfig.classes.button.style),
                        scaledBorderRadius: null,
                        scaledBorder: null,
                        scaledFontSize: null,
                    };
                case 'divider':
                    return {
                        color: {
                            enabled: true,
                            branding: null,
                            type: null,
                            value: '#000000',
                        },
                        size: 'small',
                        thickness: 'thick',
                        borderRadius: 'none',
                        computedBorderRadius: null,
                        computedHeight: null,
                        computedWidth: null,
                        scaledBorderRadius: null,
                        scaledHeight: null,
                    };
                case 'spacer':
                    return {
                        height: 50,
                        computedHeight: '0px',
                        scaledHeight: null,
                    };
                case 'text-field':
                case 'signature':
                    return {
                        label: cloneDeep(this.currentGlobalConfig.classes.text),
                        text: cloneDeep(this.currentGlobalConfig.classes.text),
                        height: null,
                        computedHeight: null,
                        scaledHeight: null,
                        scaledLabelFontSize: null,
                    };
                case 'checkbox':
                    return {
                        label: cloneDeep(this.currentGlobalConfig.classes.text),
                        scaledLabelFontSize: null,
                    };
                case 'date-stamp':
                    return {
                        label: cloneDeep(this.currentGlobalConfig.classes.text),
                        text: cloneDeep(this.currentGlobalConfig.classes.text),
                        scaledLabelFontSize: null,
                    };
                case 'radio-button':
                    return {
                        label: cloneDeep(this.currentGlobalConfig.classes.text),
                        option: cloneDeep(this.currentGlobalConfig.classes.text),
                        scaledLabelFontSize: null,
                        scaledOptionFontSize: null,
                    };
                case 'table':
                    return {
                        borderRadius: 'none',
                        headerStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#382153',
                            },

                            borderThickness: 'thick',
                            borders: ['top', 'left', 'right', 'innerV'],
                            borderColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#CAC6CB',
                            },

                            color: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },

                            textAlign: 'left',
                            fontSize: 18,
                            fontFamily: {
                                key: 'Roboto',
                                value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                            },
                        },

                        borderStyles: {
                            borderThickness: 'thick',
                            borders: ['top', 'right', 'bottom', 'left', 'innerV', 'innerH'],
                            columnBordersColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#CAC6CB',
                            },

                            rowBordersColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#CAC6CB',
                            },
                        },

                        primaryRowStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },

                            color: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#382153',
                            },

                            textAlign: 'left',
                            fontSize: 18,
                            fontFamily: {
                                key: 'Roboto',
                                value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                            },
                        },

                        secondaryRowStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },

                            color: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#382153',
                            },

                            textAlign: 'left',
                            fontSize: 18,
                            fontFamily: {
                                key: 'Roboto',
                                value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                            },
                        },

                        scaledTableBorderRadius: null,
                        scaledHeaderBorderThickness: null,
                        scaledTableBorderThickness: null,
                        scaledHeaderFontSize: null,
                        scaledPrimaryRowFontSize: null,
                        scaledSecondaryRowFontSize: null,
                    };
                case 'quote':
                    return {
                        borderRadius: 'medium',
                        headerStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#382153',
                            },

                            borders: [], // ['top', 'right','bottom','left']
                            borderColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#2ADA2B',
                            },
                            borderThickness: 'thick',
                            color: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },
                            fontSize: 18,
                            fontFamily: {
                                key: 'Roboto',
                                value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                            },
                        },

                        primaryRowStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },

                            title: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#18AEFF',
                                },
                                fontSize: 16,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },

                            description: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#877D88',
                                },
                                fontSize: 12,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },

                            calculation: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#382153',
                                },
                                fontSize: 14,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },
                        },

                        secondaryRowStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },

                            title: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#18AEFF',
                                },
                                fontSize: 16,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },

                            description: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#877D88',
                                },
                                fontSize: 12,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },

                            calculation: {
                                color: {
                                    branding: null,
                                    enabled: true,
                                    type: null,
                                    value: '#382153',
                                },
                                fontSize: 14,
                                fontFamily: {
                                    key: 'Roboto',
                                    value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                                },
                            },
                        },

                        cellBorderStyles: {
                            cellBorders: [], // ['top', 'right', 'bottom', 'left']
                            columnBordersColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#CAC6CB',
                            },
                            rowBordersColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#CAC6CB',
                            },
                            borderThickness: 'thick',
                        },

                        footerStyles: {
                            backgroundColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#382153',
                            },

                            borders: [], // ['top', 'right','bottom','left']
                            borderColor: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#2ADA2B',
                            },
                            borderThickness: 'thick',
                            color: {
                                branding: null,
                                enabled: true,
                                type: null,
                                value: '#FFFFFF',
                            },
                            fontSize: 18,
                            fontFamily: {
                                key: 'Roboto',
                                value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                            },
                        },

                        scaledTableBorderRadius: null,
                        scaledHeaderBorderThickness: null,
                        scaledCellBorderThickness: null,
                        scaledHeaderFontSize: null,
                        scaledPrimaryTitleFontSize: null,
                        scaledPrimaryDescriptionFontSize: null,
                        scaledPrimaryCalculationFontSize: null,
                        scaledSecondaryTitleFontSize: null,
                        scaledSecondaryDescriptionFontSize: null,
                        scaledSecondaryCalculationFontSize: null,
                        scaledFooterBorderThickness: null,
                        scaledFooterFontSize: null,
                    };
                default:
                    return {};
            }
        },

        elementOf(type) {
            const isElementType = camelCase(`is ${type.replaceAll('-', ' ')}`);

            return {
                type,
                [isElementType]: true,
                isFreeForm: false,
                id: this.$helpers.createId(`e-${type}`),
                style: this.elementStylesOf(type),
                freeformStyle: this.elementFreeformStylesOf(type),
                position: this.elementPosition(),
                content: this.contentOf(type),
            };
        },

        columnOf(flexGrow) {
            return {
                id: this.$helpers.createId('c'),
                type: 'column',
                elements: [],
                style: { flexGrow },
            };
        },

        sectionOf(columns, style = {}) {
            const maxColumns = columns.reduce((prev, { style: { flexGrow } }) => {
                return prev + +flexGrow;
            }, 0);

            const computedColumns = columns.reduce((acc, column) => {
                column.style.computedColumnWidth = (+column.style.flexGrow / maxColumns) * 100;
                acc.push(column);
                return acc;
            }, []);

            return {
                id: this.$helpers.createId('s'),
                type: 'section',
                columns: computedColumns,
                style: {
                    ...style,
                    hasSolidBackgroundColor:
                        this.currentGlobalConfig.classes.section.hasSolidBackgroundColor ||
                        this.currentGlobalConfig.classes.section.hasBackgroundColor,
                    backgroundColor: cloneDeep(
                        this.currentGlobalConfig.classes.section.backgroundColor,
                    ),
                    hasGradientBackground: false,
                    gradientDirection: 20,
                    gradientColor1: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#9198e5',
                    },
                    gradientColor2: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#e66465',
                    },
                    hasBackgroundImage: this.currentGlobalConfig.classes.section.hasBackgroundImage,
                    backgroundImageUrl: cloneDeep(
                        this.currentGlobalConfig.classes.section.backgroundImageUrl,
                    ),
                    alignment: 'full',
                    backgroundPosition: 'center center',
                    backgroundOpacity: 100,
                    backgroundSize: {
                        name: 'Fill Container',
                        value: 'cover',
                    },
                    backgroundRepeat: false,
                    topSpacing: 0,
                    bottomSpacing: 0,
                    computedBackground: null,
                    computedBackgroundImage: null,
                    computedBackgroundImageWidth: null,
                    scaledBackgroundImage: null,
                    scaledBackground: null,
                    scaledTopSpacing: null,
                    scaledBottomSpacing: null,
                },
            };
        },

        createNewHeader(style = {}, documentName) {
            return {
                id: this.$helpers.createId('h'),
                type: 'header',
                isGlobalVisible: false,
                content: {
                    hasDocumentName: true,
                    text: documentName,
                    hasLogo: true,
                    logo: this.brandingContext.logoBrandingOptions[0] || null,
                },
                style: {
                    ...style,
                    ...this.currentGlobalConfig.classes.header,
                    hasSolidBackgroundColor: true,
                    backgroundColor: {
                        ...cloneDeep(this.currentGlobalConfig.classes.header.backgroundColor),
                        value: '#ffffff',
                    },
                    hasGradientBackground: false,
                    gradientDirection: 20,
                    gradientColor1: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#9198e5',
                    },
                    gradientColor2: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#e66465',
                    },
                    hasBackgroundImage: this.currentGlobalConfig.classes.header.hasBackgroundImage,
                    backgroundImageUrl: cloneDeep(
                        this.currentGlobalConfig.classes.header.backgroundImageUrl,
                    ),
                    backgroundPosition: 'center center',
                    backgroundOpacity: 100,
                    backgroundSize: {
                        name: 'Fill Container',
                        value: 'cover',
                    },
                    backgroundRepeat: false,
                    computedBackground: null,
                    computedBackgroundImage: null,
                    scaledFontSize: null,
                },
            };
        },

        createNewFooter(style = {}) {
            return {
                id: this.$helpers.createId('f'),
                type: 'footer',
                isGlobalVisible: false,
                content: {
                    hasMessage: true,
                    messageText: 'Type your confidentiality message here',
                    hasPageNumbering: true,
                },
                style: {
                    ...style,
                    ...this.currentGlobalConfig.classes.footer,
                    hasSolidBackgroundColor: true,
                    backgroundColor: {
                        ...cloneDeep(this.currentGlobalConfig.classes.footer.backgroundColor),
                        value: '#ffffff',
                    },
                    hasGradientBackground: false,
                    gradientDirection: 20,
                    gradientColor1: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#9198e5',
                    },
                    gradientColor2: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#e66465',
                    },
                    hasBackgroundImage: this.currentGlobalConfig.classes.footer.hasBackgroundImage,
                    backgroundImageUrl: cloneDeep(
                        this.currentGlobalConfig.classes.footer.backgroundImageUrl,
                    ),
                    backgroundPosition: 'center center',
                    backgroundOpacity: 100,
                    backgroundSize: {
                        name: 'Fill Container',
                        value: 'cover',
                    },
                    backgroundRepeat: false,
                    computedBackground: null,
                    computedBackgroundImage: null,
                    scaledMessageFontSize: null,
                    scaledPageNumFontSize: null,
                },
            };
        },

        createNewPage(style = {}) {
            return {
                id: this.$helpers.createId('p'),
                type: 'page',
                pageType: 'structured',
                sections: [],
                header: cloneDeep(this.config.header),
                footer: cloneDeep(this.config.footer),
                isIndividualHeaderVisible: this.config.header
                    ? this.config.header.isGlobalVisible
                    : false,
                isIndividualFooterVisible: this.config.footer
                    ? this.config.footer.isGlobalVisible
                    : false,
                style: {
                    ...style,
                    hasSolidBackgroundColor:
                        this.currentGlobalConfig.classes.page.hasSolidBackgroundColor ||
                        this.currentGlobalConfig.classes.page.hasBackgroundColor, // used by older documents
                    backgroundColor: cloneDeep(
                        this.currentGlobalConfig.classes.page.backgroundColor,
                    ),
                    hasGradientBackground: false,
                    gradientDirection: 20,
                    gradientColor1: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#9198e5',
                    },
                    gradientColor2: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#e66465',
                    },
                    hasBackgroundImage: this.currentGlobalConfig.classes.page.hasBackgroundImage,
                    backgroundImageUrl: cloneDeep(
                        this.currentGlobalConfig.classes.page.backgroundImageUrl,
                    ),
                    alignment: 'full',
                    computedBackground: null,
                },
            };
        },

        createNewFreeFormPage(style = {}, backgroundUrl = null) {
            return {
                id: this.$helpers.createId('p'),
                type: 'page',
                pageType: 'freeform',
                sections: null,
                elements: [],
                style: {
                    ...style,
                    hasSolidBackgroundColor: backgroundUrl
                        ? true
                        : this.currentGlobalConfig.classes.page.hasSolidBackgroundColor ||
                          this.currentGlobalConfig.classes.page.hasBackgroundColor, // used by older documents
                    backgroundColor: backgroundUrl
                        ? {
                              branding: null,
                              enabled: true,
                              type: null,
                              value: '#FFFFFF',
                          }
                        : cloneDeep(this.currentGlobalConfig.classes.page.backgroundColor),
                    hasGradientBackground: false,
                    gradientDirection: 20,
                    gradientColor1: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#9198e5',
                    },
                    gradientColor2: {
                        branding: null,
                        enabled: false,
                        type: null,
                        value: '#e66465',
                    },
                    hasBackgroundImage: backgroundUrl
                        ? true
                        : this.currentGlobalConfig.classes.page.hasBackgroundImage,
                    backgroundImageUrl: backgroundUrl
                        ? backgroundUrl
                        : cloneDeep(this.currentGlobalConfig.classes.page.backgroundImageUrl),
                    alignment: 'full',
                    computedBackground: null,
                },
            };
        },

        cloneElementContentOf(type, content) {
            switch (type) {
                case 'text-field':
                case 'date-stamp':
                    return {
                        ...cloneDeep(content),
                        recipient: null,
                        body: null,
                    };
                case 'signature':
                    return {
                        ...cloneDeep(content),
                        recipient: null,
                        link: null,
                    };
                case 'checkbox':
                    return {
                        ...cloneDeep(content),
                        recipient: null,
                        isChecked: false,
                    };
                case 'radio-button':
                    return {
                        ...cloneDeep(content),
                        options: content.options.map((option) => ({
                            id: this.$helpers.createId('opt'),
                            value: option.value,
                        })),
                        recipient: null,
                        selectedOption: null,
                        recipientSelected: false,
                    };
                case 'table':
                    return {
                        ...cloneDeep(content),
                        headerRow: {
                            ...cloneDeep(content.headerRow),
                            id: this.$helpers.createId('hr'),
                            columns: content.headerRow.columns.map((column) => ({
                                ...cloneDeep(column),
                                id: this.$helpers.createId('th'),
                            })),
                        },

                        tableRows: content.tableRows.map((row) => ({
                            ...cloneDeep(row),
                            id: this.$helpers.createId('tr'),
                            columns: row.columns.map((column) => ({
                                ...cloneDeep(column),
                                id: this.$helpers.createId('td'),
                            })),
                        })),
                    };
                default:
                    return cloneDeep(content);
            }
        },

        cloneElement(element) {
            return {
                ...element,
                freeformStyle: cloneDeep(element.freeformStyle),
                style: cloneDeep(element.style),
                content: this.cloneElementContentOf(element.type, element.content),
                id: this.$helpers.createId(`e-${element.type}`),
            };
        },

        cloneColumn(column) {
            return {
                ...column,
                elements: column.elements.map(this.cloneElement),
                style: cloneDeep(column.style),
                id: this.$helpers.createId('c'),
            };
        },

        cloneSection(section) {
            return {
                ...section,
                style: cloneDeep(section.style),
                id: this.$helpers.createId('s'),
                columns: section.columns.map(this.cloneColumn),
            };
        },

        cloneHeader(header) {
            return {
                ...header,
                style: cloneDeep(header.style),
                id: this.$helpers.createId('h'),
            };
        },

        cloneFooter(footer) {
            return {
                ...footer,
                style: cloneDeep(footer.style),
                id: this.$helpers.createId('f'),
            };
        },

        clonePage(page) {
            if (page.pageType === 'freeform') {
                return {
                    ...page,
                    style: cloneDeep(page.style),
                    id: this.$helpers.createId('p'),
                    elements: page.elements.map(this.cloneElement),
                };
            }
            return {
                ...page,
                style: cloneDeep(page.style),
                id: this.$helpers.createId('p'),
                sections: page.sections.map(this.cloneSection),
                header: page.header && this.cloneHeader(page.header),
                footer: page.footer && this.cloneFooter(page.footer),
            };
        },

        getFontSizes() {
            let fontSizes = [];
            for (let i = 12; i <= 72; i += 2) {
                fontSizes.push(i.toString());
            }
            return fontSizes;
        },

        getLineHeights() {
            return ['4', '5', '6', '7', ...this.getFontSizes()];
        },

        getDefaultLogo() {
            let defaultLogo = find(
                this.brandingContext.logoBrandingOptions,
                (option) => !isNil(option.src),
            ); // Looks for the first existing logo via src
            if (defaultLogo) {
                return cloneDeep(defaultLogo);
            } else {
                return cloneDeep(this.brandingContext.logoBrandingOptions[0]); // Initialize as darkLogo
            }
        },

        setReactiveProperty(obj, prop, value) {
            isNil(obj[prop]) || obj[prop] === 'undefined' ? this.$set(obj, prop, value) : noop();
        },

        replaceOldProperty(obj, oldProp, newProp) {
            this.$set(obj, newProp, cloneDeep(obj[oldProp]));
            this.$delete(obj, oldProp);
        },

        // Branding

        getBranding() {
            return new Promise((resolve) => {
                this.getBrandingConfiguration('Document')
                    .then((config) => {
                        this.brandingContext.brandingOptions =
                            this.$helpers.createDocumentBrandingOptions(config);
                        this.brandingContext.logoBrandingOptions =
                            this.$helpers.createDocumentLogoBrandingOptions(config);
                        const isBrandingSetup = true;
                        resolve([config, isBrandingSetup]);
                    })
                    .catch(() => {
                        const defaultConfig = this.$helpers.getDefaultDocumentBranding();
                        this.brandingContext.brandingOptions =
                            this.$helpers.getDefaultDocumentBrandingOptions();
                        this.brandingContext.logoBrandingOptions =
                            this.$helpers.getDefaultDocumentLogoBrandingOptions();
                        const isBrandingSetup = false;
                        resolve([defaultConfig, isBrandingSetup]);
                    });
            });
        },

        processPage(page) {
            this.updateColor(page.style.backgroundColor);
            this.updateColor(page.style.gradientColor1);
            this.updateColor(page.style.gradientColor2);
        },

        processHeader(header) {
            // Required to update older headers with the new values for header settings probably remove this down the road.
            this.setReactiveProperty(header.style, 'backgroundPosition', 'center center');
            this.setReactiveProperty(header.style, 'backgroundOpacity', 100);
            this.setReactiveProperty(header.style, 'backgroundSize', {
                name: 'Fill Container',
                value: 'cover',
            });
            this.setReactiveProperty(header.style, 'backgroundRepeat', false);
            this.setReactiveProperty(header.style, 'computedBackgroundImage', null);

            // Branding Processing
            this.updateColor(header.style.backgroundColor);
            this.updateColor(header.style.gradientColor1);
            this.updateColor(header.style.gradientColor2);
            this.updateLogo(header.content.logo);
        },

        processFooter(footer) {
            // Required to update older footers with the new values for footer settings probably remove this down the road.
            this.setReactiveProperty(footer.style, 'backgroundPosition', 'center center');
            this.setReactiveProperty(footer.style, 'backgroundOpacity', 100);
            this.setReactiveProperty(footer.style, 'backgroundSize', {
                name: 'Fill Container',
                value: 'cover',
            });
            this.setReactiveProperty(footer.style, 'backgroundRepeat', false);
            this.setReactiveProperty(footer.style, 'computedBackgroundImage', null);

            // Branding Processing
            this.updateColor(footer.style.backgroundColor);
            this.updateColor(footer.style.gradientColor1);
            this.updateColor(footer.style.gradientColor2);
        },

        processSection(section) {
            // Required to update older sections with the new values for section settings probably remove this down the road.
            this.setReactiveProperty(
                section.style,
                'hasSolidBackgroundColor',
                !section.style.hasGradientBackground,
            );
            this.setReactiveProperty(section.style, 'backgroundPosition', 'center center');
            this.setReactiveProperty(section.style, 'backgroundOpacity', 100);
            this.setReactiveProperty(section.style, 'backgroundSize', {
                name: 'Fill Container',
                value: 'cover',
            });
            this.setReactiveProperty(section.style, 'backgroundRepeat', false);
            this.setReactiveProperty(section.style, 'computedBackgroundImage', null);
            this.setReactiveProperty(section.style, 'computedBackgroundImageWidth', null);
            this.setReactiveProperty(section.style, 'scaledBackgroundImage', null);

            // Branding processing
            this.updateColor(section.style.backgroundColor);
            this.updateColor(section.style.gradientColor1);
            this.updateColor(section.style.gradientColor2);
        },

        processElement(unprocessedElement, isFreeForm = false) {
            this.setReactiveProperty(unprocessedElement, 'isFreeForm', isFreeForm);
            this.setReactiveProperty(
                unprocessedElement,
                'freeformStyle',
                this.elementFreeformStylesOf(unprocessedElement.type),
            );

            this.setReactiveProperty(unprocessedElement, 'position', this.elementPosition());

            switch (unprocessedElement.type) {
                case 'button':
                    this.processButtonBranding(unprocessedElement);
                    break;
                case 'divider':
                    this.processDividerBranding(unprocessedElement);
                    break;
                case 'text':
                    unprocessedElement.content.body = this.processTextBranding(
                        unprocessedElement.content.body,
                    );
                    break;
                case 'text-field':
                case 'signature':
                case 'date-stamp':
                    this.updateColor(unprocessedElement.style.label.color);
                    this.updateColor(unprocessedElement.style.text.color);
                    break;
                case 'checkbox':
                    this.processCheckboxBranding(unprocessedElement);
                    break;
                case 'radio-button':
                    this.updateColor(unprocessedElement.style.label.color);
                    this.updateColor(unprocessedElement.style.option.color);
                    break;
                case 'quote':
                    this.processQuoteBranding(unprocessedElement);
                    break;
                case 'table':
                    this.processTableBranding(unprocessedElement);
                    break;
                case 'logo':
                    this.processLogoBranding(unprocessedElement);
                    break;
                default:
                    break;
            }
        },

        processDividerBranding(unprocessedDivider) {
            this.updateColor(unprocessedDivider.style.color);
        },

        processButtonBranding(unprocessedButton) {
            this.updateColor(unprocessedButton.style.textColor);
            this.updateColor(unprocessedButton.style.buttonColor);
        },

        processCheckboxBranding(unprocessedCheckbox) {
            // Required to update older checkboxes with no label
            const htmlTagRegex = /<[^>]*>/g;
            this.setReactiveProperty(unprocessedCheckbox.content, 'hasLabel', false);
            this.setReactiveProperty(
                unprocessedCheckbox.content,
                'message',
                cloneDeep(unprocessedCheckbox.content.labelText),
            );

            if (htmlTagRegex.test(unprocessedCheckbox.content.labelText)) {
                unprocessedCheckbox.content.labelText = 'Enter Label';
            }

            if (isEmpty(unprocessedCheckbox.style)) {
                unprocessedCheckbox.style = {
                    label: cloneDeep(this.currentGlobalConfig.classes.text),
                    scaledLabelFontSize: null,
                };
            }

            // Actual branding processing
            unprocessedCheckbox.content.message = this.processTextBranding(
                unprocessedCheckbox.content.message,
            );
            this.updateColor(unprocessedCheckbox.style.label.color);
        },

        processTextBranding(unprocessedElementBody) {
            const el = document.createElement('span');
            el.innerHTML = unprocessedElementBody;

            this.brandingContext.brandingOptions.forEach((brandingOption) => {
                const selector = `[data-branding='${brandingOption.key}']`;
                const elements = el.querySelectorAll(selector);

                elements.forEach((element) => {
                    element.style.color = brandingOption.color;
                });
            });

            return el.innerHTML;
        },

        processQuoteBranding(unprocessedQuote) {
            // Required this to update older quotes with the new style and content values needed for new quote settings.
            const defaultRowStyles = {
                title: {
                    color: {
                        branding: null,
                        enabled: true,
                        type: null,
                        value: '#18AEFF',
                    },
                    fontSize: 16,
                    fontFamily: {
                        key: 'Roboto',
                        value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                    },
                },

                description: {
                    color: {
                        branding: null,
                        enabled: true,
                        type: null,
                        value: '#877D88',
                    },
                    fontSize: 12,
                    fontFamily: {
                        key: 'Roboto',
                        value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                    },
                },

                calculation: {
                    color: {
                        branding: null,
                        enabled: true,
                        type: null,
                        value: '#382153',
                    },
                    fontSize: 14,
                    fontFamily: {
                        key: 'Roboto',
                        value: `Roboto, Helvetica, Arial, Tahoma, 'Franklin Gothic Medium', sans-serif`,
                    },
                },
            };

            hasIn(unprocessedQuote.style, 'primaryCellStyles')
                ? this.replaceOldProperty(
                      unprocessedQuote.style,
                      'primaryCellStyles',
                      'primaryRowStyles',
                  )
                : noop();

            hasIn(unprocessedQuote.style, 'secondaryCellStyles')
                ? this.replaceOldProperty(
                      unprocessedQuote.style,
                      'secondaryCellStyles',
                      'secondaryRowStyles',
                  )
                : noop();

            hasIn(unprocessedQuote.style, 'scaledPrimaryCellFontSize')
                ? delete unprocessedQuote.style.scaledPrimaryCellFontSize
                : noop();

            hasIn(unprocessedQuote.style, 'scaledSecondaryCellFontSize')
                ? delete unprocessedQuote.style.scaledSecondaryCellFontSize
                : noop();

            this.setReactiveProperty(
                unprocessedQuote.style.primaryRowStyles,
                'title',
                defaultRowStyles.title,
            );

            this.setReactiveProperty(
                unprocessedQuote.style.primaryRowStyles,
                'description',
                defaultRowStyles.description,
            );
            this.setReactiveProperty(
                unprocessedQuote.style.primaryRowStyles,
                'calculation',
                defaultRowStyles.calculation,
            );
            this.setReactiveProperty(
                unprocessedQuote.style.secondaryRowStyles,
                'title',
                defaultRowStyles.title,
            );
            this.setReactiveProperty(
                unprocessedQuote.style.secondaryRowStyles,
                'description',
                defaultRowStyles.description,
            );
            this.setReactiveProperty(
                unprocessedQuote.style.secondaryRowStyles,
                'calculation',
                defaultRowStyles.calculation,
            );
            this.setReactiveProperty(unprocessedQuote.style, 'scaledPrimaryTitleFontSize', null);
            this.setReactiveProperty(
                unprocessedQuote.style,
                'scaledPrimaryDescriptionFontSize',
                null,
            );
            this.setReactiveProperty(
                unprocessedQuote.style,
                'scaledPrimaryCalculationFontSize',
                null,
            );
            this.setReactiveProperty(unprocessedQuote.style, 'scaledSecondaryTitleFontSize', null);
            this.setReactiveProperty(
                unprocessedQuote.style,
                'scaledSecondaryDescriptionFontSize',
                null,
            );
            this.setReactiveProperty(
                unprocessedQuote.style,
                'scaledSecondaryCalculationFontSize',
                null,
            );

            this.setReactiveProperty(unprocessedQuote.content, 'quoteView', {
                showUpfront: true,
                showMonthly: true,
                showQuarterly: true,
                showSemiAnnual: true,
                showAnnual: true,
                showTax: true,
                showDiscount: true,
                showTotal: true,
                showInitialPayment: true,
            });

            this.setReactiveProperty(unprocessedQuote.content, 'solutionsView', {
                showName: true,
                showDescription: true,
                showPrice: true,
                showQuantity: true,
                showTerm: true,
                showDiscount: true,
                showSubtotal: true,
                showProductImage: true,
            });

            unprocessedQuote.content.solutions.forEach((solution) => {
                this.setReactiveProperty(solution, 'term', 1);
                this.setReactiveProperty(solution, 'viewConfig', {
                    showName: true,
                    showDescription: true,
                    showPrice: true,
                    showQuantity: true,
                    showTerm: true,
                    showDiscount: true,
                    showSubtotal: true,
                    showProductImage: true,
                });
            });

            this.setReactiveProperty(unprocessedQuote.style.footerStyles, 'borders', []);
            this.setReactiveProperty(
                unprocessedQuote.style.footerStyles,
                'borderThickness',
                'thick',
            );
            this.setReactiveProperty(unprocessedQuote.style.footerStyles, 'backgroundColor', {
                branding: null,
                enabled: true,
                type: null,
                value: '#382153',
            });
            this.setReactiveProperty(unprocessedQuote.style.footerStyles, 'borderColor', {
                branding: null,
                enabled: true,
                type: null,
                value: '#2ADA2B',
            });
            this.setReactiveProperty(unprocessedQuote.style, 'scaledFooterBorderThickness', null);
            this.setReactiveProperty(unprocessedQuote.style, 'scaledFooterFontSize', null);

            // Actual branding processing
            this.updateColor(unprocessedQuote.style.headerStyles.backgroundColor);
            this.updateColor(unprocessedQuote.style.headerStyles.borderColor);
            this.updateColor(unprocessedQuote.style.headerStyles.color);
            this.updateColor(unprocessedQuote.style.footerStyles.backgroundColor);
            this.updateColor(unprocessedQuote.style.footerStyles.borderColor);
            this.updateColor(unprocessedQuote.style.footerStyles.color);
            this.updateColor(unprocessedQuote.style.primaryRowStyles.backgroundColor);
            this.updateColor(unprocessedQuote.style.primaryRowStyles.title.color);
            this.updateColor(unprocessedQuote.style.primaryRowStyles.description.color);
            this.updateColor(unprocessedQuote.style.primaryRowStyles.calculation.color);
            this.updateColor(unprocessedQuote.style.secondaryRowStyles.backgroundColor);
            this.updateColor(unprocessedQuote.style.secondaryRowStyles.title.color);
            this.updateColor(unprocessedQuote.style.secondaryRowStyles.description.color);
            this.updateColor(unprocessedQuote.style.secondaryRowStyles.calculation.color);
            this.updateColor(unprocessedQuote.style.cellBorderStyles.columnBordersColor);
            this.updateColor(unprocessedQuote.style.cellBorderStyles.rowBordersColor);
        },

        processTableBranding(unprocessedTable) {
            this.updateColor(unprocessedTable.style.headerStyles.backgroundColor);
            this.updateColor(unprocessedTable.style.headerStyles.borderColor);
            this.updateColor(unprocessedTable.style.headerStyles.color);
            this.updateColor(unprocessedTable.style.primaryRowStyles.backgroundColor);
            this.updateColor(unprocessedTable.style.primaryRowStyles.color);
            this.updateColor(unprocessedTable.style.secondaryRowStyles.backgroundColor);
            this.updateColor(unprocessedTable.style.secondaryRowStyles.color);
            this.updateColor(unprocessedTable.style.borderStyles.columnBordersColor);
            this.updateColor(unprocessedTable.style.borderStyles.rowBordersColor);
            unprocessedTable.content.headerRow.columns.forEach((column) => {
                this.processTextBranding(column.content);
            });
            unprocessedTable.content.tableRows.forEach((row) => {
                row.columns.forEach((column) => {
                    this.processTextBranding(column.content);
                });
            });
        },

        processLogoBranding(unprocessedLogo) {
            const currentLogo = cloneDeep(unprocessedLogo.content.logo);
            this.updateLogo(unprocessedLogo.content.logo);
            if (!isEqual(currentLogo, unprocessedLogo.content.logo)) {
                unprocessedLogo.style = {
                    ...unprocessedLogo.style,
                    width: 0,
                    height: 'auto',
                    maxWidth: 0,
                    scaledWidth: null,
                };
            }
        },

        updateColor(brandingColorSetting) {
            if (brandingColorSetting) {
                if (
                    brandingColorSetting.type &&
                    brandingColorSetting.type.toUpperCase() === 'BRANDED'
                ) {
                    const { color } = this.brandingContext.brandingOptions.find(
                        (brandingOption) => brandingOption.key === brandingColorSetting.branding,
                    );
                    brandingColorSetting.value = color;
                }
            }
        },

        updateLogo(logo) {
            if (logo) {
                const { imgName, src } = this.brandingContext.logoBrandingOptions.find(
                    ({ key }) => key === logo.key,
                );
                logo.imgName = imgName;
                logo.src = src;
            }
        },

        processGlobalSettingsBranding(globalSettings) {
            const {
                heading1,
                heading2,
                heading3,
                heading4,
                heading5,
                heading6,
                text,
                link,
                button,
                canvas,
                section,
                page,
                bulletsColor,
            } = globalSettings.classes;

            this.setReactiveProperty(page, 'margins', {
                showMargins: false,
                allSides: {
                    active: false,
                    value: 80,
                },
                perSide: {
                    active: false,
                    top: 80,
                    left: 80,
                    bottom: 80,
                    right: 80,
                },
            });

            const brandingSettings = this.$helpers.getDefaultDocumentBranding();

            for (let prop in globalSettings.classes) {
                if (JSON.stringify(globalSettings.classes[prop]) === '{}') {
                    if (prop === 'link') {
                        globalSettings.classes[prop] = brandingSettings['links'];
                    } else {
                        globalSettings.classes[prop] = brandingSettings[prop];
                    }
                }
            }

            this.updateColor(heading1.color);
            this.updateColor(heading2.color);
            this.updateColor(heading3.color);
            this.updateColor(heading4.color);
            this.updateColor(heading5.color);
            this.updateColor(heading6.color);
            this.updateColor(text.color);
            this.updateColor(link.color);
            this.updateColor(canvas.backgroundColor);
            this.updateColor(page.backgroundColor);
            this.updateColor(section.backgroundColor);
            this.processButtonBranding(button);
            this.updateColor(bulletsColor);
        },
    },
};
